/**
 * DocumentGallery Component
 * Display documents for an entity using RGPD-compliant hierarchy
 * Per docstore.yaml specification and ACCORDION_UI_PATTERN.md
 *
 * Structure (for entity modals - entity is already known):
 * ▼ ACTIFS                                    [3 docs]
 *    ├─ 10_REGISTRATION/ (Immatriculation)    [1]
 *    ├─ 30_INSURANCE/ (Assurance)             [1]
 *    └─ 90_MISC/ (Divers)                     [1]
 * ► ARCHIVES                                  [1 doc]
 *    └─ 30_INSURANCE/ (carte verte expiree)   [1]
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  File, Download, Trash2, AlertTriangle,
  Loader2, RefreshCw, ChevronDown, ChevronRight, Folder, FolderOpen,
  FileWarning, HardDrive, Archive, CheckCircle,
  Eye
} from 'lucide-react';
import { documentAPI } from '../../api.js';
import {
  getVisibleFolders,
  getFolder,
  getDocumentTypeLabel,
  getArchiveStatus
} from '../../config/docstore.js';
import DocumentItem, { formatSize, getExpiryStatus } from '../documents/DocumentItem.jsx';

/**
 * FolderAccordion - Extracted to module scope to prevent re-creation on every render
 * This fixes event handler binding issues that occur with nested component definitions.
 */
function FolderAccordion({
  folderCode,
  folderData,
  statusKey,
  isArchived,
  isExpanded,
  onToggle,
  onValidate,
  onCardClick,
  renderActions,
  deletingId
}) {
  const hasDocuments = folderData.docs.length > 0;

  if (!hasDocuments) return null;

  return (
    <div className={`border rounded-lg overflow-hidden ${
      isArchived ? 'border-gray-200' : folderData.warningCount > 0 ? 'border-orange-200' : 'border-gray-200'
    }`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full px-4 py-2.5 flex items-center justify-between transition-colors ${
          isExpanded ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}

          {isExpanded ? (
            <FolderOpen className="w-4 h-4 text-blue-500" />
          ) : (
            <Folder className="w-4 h-4 text-amber-500" />
          )}

          <code className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">
            {folderCode}/
          </code>

          <span className="font-medium text-sm text-gray-900">
            {folderData.config.label}
          </span>

          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            isExpanded ? 'bg-blue-200 text-blue-800' : folderData.config.color
          }`}>
            {folderData.docs.length}
          </span>

          {!isArchived && folderData.warningCount > 0 && (
            <AlertTriangle className="w-4 h-4 text-orange-500" />
          )}
        </div>

        <span className="text-xs text-gray-500">
          {formatSize(folderData.totalSize)}
        </span>
      </button>

      {isExpanded && (
        <div className="p-3 space-y-2 bg-white border-t border-gray-100">
          {folderData.docs.map(doc => (
            <DocumentItem
              key={doc.id}
              doc={doc}
              isArchived={isArchived}
              actionsPosition="inline"
              actionsSlot={renderActions(doc, isArchived)}
              onCardClick={onCardClick}
              onValidate={onValidate}
              typeLabel={getDocumentTypeLabel(doc.document_type)}
              archiveReasonKey="archiveReason"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * DocumentGallery Component - RGPD-compliant Accordion
 */
export default function DocumentGallery({
  scope,
  referenceId,
  onDocumentClick,
  onDelete,
  onRefresh,
  onValidate, // Callback when user wants to view/validate a document (opens parent's modal)
  documents: externalDocs,
  loading: externalLoading,
  hideArchived = false, // deprecated — use sections instead
  sections: sectionsProp,
}) {
  const { t } = useTranslation();
  // Section visibility: sections prop wins, hideArchived is legacy fallback
  const sections = sectionsProp || { active: true, archived: !hideArchived };
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedStatus, setExpandedStatus] = useState(null); // 'active', 'archived', or null - ALL CLOSED by default
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [deletingId, setDeletingId] = useState(null);

  // Get folder structure for this scope
  const folders = useMemo(() => getVisibleFolders(scope), [scope]);

  // Fetch documents
  const fetchDocuments = async () => {
    setLoading(true);
    try {
      let result;
      if (scope === 'VEHICLE') {
        result = await documentAPI.getForVehicle(referenceId);
      } else if (scope === 'DRIVER') {
        result = await documentAPI.getForDriver(referenceId);
      } else if (scope === 'CLAIM') {
        result = await documentAPI.getForClaim(referenceId);
      } else {
        result = await documentAPI.getAll({ scope, reference_id: referenceId });
      }

      if (result.ok) {
        setDocuments(result.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (externalDocs) {
      setDocuments(externalDocs);
    } else if (referenceId) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId, externalDocs]);

  // ALL ACCORDIONS CLOSED BY DEFAULT
  // Auto-expand removed per requirement - users must click to expand

  // Handle download - useCallback to keep stable reference
  const handleDownload = useCallback(async (doc, e) => {
    e?.stopPropagation();
    try {
      await documentAPI.download(doc.id, doc.original_filename || doc.filename);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, []);

  // Handle delete - useCallback to keep stable reference
  const handleDelete = useCallback(async (doc, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('documents.confirm.delete', { name: doc.original_filename || doc.filename }))) return;

    setDeletingId(doc.id);
    try {
      const result = await documentAPI.delete(doc.id, 'User deletion from gallery');
      if (result.ok) {
        setDocuments(prev => prev.filter(d => d.id !== doc.id));
        onDelete?.(doc);
      }
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  }, [onDelete, t]);

  // Toggle status group (exclusive)
  const toggleStatus = useCallback((status) => {
    setExpandedStatus(prev => prev === status ? null : status);
  }, []);

  // Toggle folder (non-exclusive)
  const toggleFolder = useCallback((folderKey) => {
    setExpandedFolders(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(folderKey)) {
        newExpanded.delete(folderKey);
      } else {
        newExpanded.add(folderKey);
      }
      return newExpanded;
    });
  }, []);

  // Group documents by archive status, then by folder
  const groupedData = useMemo(() => {
    const active = { docs: [], byFolder: {}, totalSize: 0, warningCount: 0 };
    const archived = { docs: [], byFolder: {}, totalSize: 0 };

    // Initialize folders
    folders.forEach(folder => {
      active.byFolder[folder.code] = { config: folder, docs: [], totalSize: 0, warningCount: 0 };
      archived.byFolder[folder.code] = { config: folder, docs: [], totalSize: 0 };
    });

    documents.forEach(doc => {
      const { isArchived, reason } = getArchiveStatus(doc);
      const folderCode = getFolder(scope, doc.document_type);
      const target = isArchived ? archived : active;

      // Add archive reason to doc for display
      const docWithReason = isArchived ? { ...doc, archiveReason: reason } : doc;

      target.docs.push(docWithReason);
      target.totalSize += doc.file_size || 0;

      // Add to folder
      let folder = target.byFolder[folderCode];
      if (!folder) {
        const miscCode = Object.keys(target.byFolder).find(k => k.includes('MISC') || k.includes('90_'));
        folder = target.byFolder[miscCode];
      }
      if (folder) {
        folder.docs.push(docWithReason);
        folder.totalSize += doc.file_size || 0;

        // Count warnings (only for active)
        if (!isArchived) {
          const expiry = getExpiryStatus(doc.valid_to);
          if (expiry && (expiry.status === 'critical' || expiry.status === 'warning')) {
            active.warningCount++;
            folder.warningCount = (folder.warningCount || 0) + 1;
          }
        }
      }
    });

    return { active, archived };
  }, [documents, folders, scope]);

  const isLoading = loading || externalLoading;

  // Render actions slot for DocumentItem - useCallback for stable reference
  // MUST be defined before any early returns to follow Rules of Hooks
  const renderGalleryActions = useCallback((doc, isArchived) => {
    const canPreview = doc.mime_type === 'application/pdf' || doc.mime_type?.startsWith('image/');
    const isDeleting = deletingId === doc.id;

    return (
      <div className="flex items-center gap-2">
        {/* View button - always visible for previewable docs */}
        {canPreview && onValidate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onValidate(doc);
            }}
            className="px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
            title={t('documents.item.view')}
          >
            <Eye className="w-4 h-4" />
            <span>{t('common.actions.view')}</span>
          </button>
        )}
        {/* Download and delete - visible on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => handleDownload(doc, e)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title={t('common.actions.download')}
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => handleDelete(doc, e)}
            disabled={isDeleting}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            title={t('common.actions.delete')}
          >
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  }, [deletingId, onValidate, handleDownload, handleDelete, t]);

  // Custom card click handler - useCallback for stable reference
  const handleGalleryCardClick = useCallback((doc, e) => {
    const canPreview = doc.mime_type === 'application/pdf' || doc.mime_type?.startsWith('image/');
    if (canPreview && onValidate) {
      onValidate(doc);
    } else {
      handleDownload(doc, e);
    }
  }, [onValidate, handleDownload]);

  // Empty state - placed after all hooks to follow Rules of Hooks
  if (!isLoading && documents.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
        <HardDrive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500 font-medium">{t('documents.empty.title')}</p>
        <p className="text-sm text-gray-400 mt-1">{t('common.docs.gallery.emptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">
            {t('common.docs.gallery.count', { count: documents.length })}
            <span className="text-gray-400 ml-1">({formatSize(groupedData.active.totalSize + groupedData.archived.totalSize)})</span>
          </span>
          {groupedData.active.warningCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
              <AlertTriangle className="w-3 h-3" />
              {t('documents.list.toRenew', { count: groupedData.active.warningCount })}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { onRefresh?.(); fetchDocuments(); }}
          disabled={isLoading}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title={t('common.actions.refresh')}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Two-level accordion: Status → Folders */}
      {!isLoading && (
        <div className="space-y-3">
          {/* ACTIFS Section */}
          <div className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
            groupedData.active.warningCount > 0 ? 'border-orange-200' : 'border-green-200'
          }`}>
            <button
              type="button"
              onClick={() => toggleStatus('active')}
              className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                expandedStatus === 'active' ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                {expandedStatus === 'active' ? (
                  <ChevronDown className="w-5 h-5 text-green-600" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}

                <CheckCircle className="w-5 h-5 text-green-600" />

                <span className="font-semibold text-green-900">{t('documents.list.active')}</span>

                <span className={`px-2.5 py-1 rounded-full text-sm font-medium ${
                  expandedStatus === 'active' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'
                }`}>
                  {groupedData.active.docs.length}
                </span>

                {groupedData.active.warningCount > 0 && (
                  <span className="flex items-center gap-1 text-orange-600">
                    <FileWarning className="w-4 h-4" />
                    <span className="text-xs font-medium">{t('documents.list.toRenew', { count: groupedData.active.warningCount })}</span>
                  </span>
                )}
              </div>

              <span className="text-sm text-gray-500">
                {formatSize(groupedData.active.totalSize)}
              </span>
            </button>

            {expandedStatus === 'active' && (
              <div className="p-3 space-y-2 bg-white">
                {Object.entries(groupedData.active.byFolder)
                  .filter(([, data]) => data.docs.length > 0)
                  .map(([code, data]) => (
                    <FolderAccordion
                      key={code}
                      folderCode={code}
                      folderData={data}
                      statusKey="active"
                      isArchived={false}
                      isExpanded={expandedFolders.has(`active-${code}`)}
                      onToggle={() => toggleFolder(`active-${code}`)}
                      onValidate={onValidate}
                      onCardClick={handleGalleryCardClick}
                      renderActions={renderGalleryActions}
                      deletingId={deletingId}
                    />
                  ))}
                {groupedData.active.docs.length === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <File className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('documents.list.noActiveDoc')}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ARCHIVES Section */}
          {sections.archived !== false && <div className="bg-white rounded-lg shadow-sm border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleStatus('archived')}
              className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                expandedStatus === 'archived' ? 'bg-gray-100 hover:bg-gray-150' : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                {expandedStatus === 'archived' ? (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}

                <Archive className="w-5 h-5 text-gray-500" />

                <span className="font-semibold text-gray-700">{t('documents.list.archived')}</span>

                <span className={`px-2.5 py-1 rounded-full text-sm font-medium ${
                  expandedStatus === 'archived' ? 'bg-gray-300 text-gray-800' : 'bg-gray-200 text-gray-600'
                }`}>
                  {groupedData.archived.docs.length}
                </span>

                <span className="text-xs text-gray-500">
                  {t('common.docs.gallery.archivedHint')}
                </span>
              </div>

              <span className="text-sm text-gray-400">
                {formatSize(groupedData.archived.totalSize)}
              </span>
            </button>

            {expandedStatus === 'archived' && (
              <div className="p-3 space-y-2 bg-gray-50">
                {Object.entries(groupedData.archived.byFolder)
                  .filter(([, data]) => data.docs.length > 0)
                  .map(([code, data]) => (
                    <FolderAccordion
                      key={code}
                      folderCode={code}
                      folderData={data}
                      statusKey="archived"
                      isArchived={true}
                      isExpanded={expandedFolders.has(`archived-${code}`)}
                      onToggle={() => toggleFolder(`archived-${code}`)}
                      onValidate={onValidate}
                      onCardClick={handleGalleryCardClick}
                      renderActions={renderGalleryActions}
                      deletingId={deletingId}
                    />
                  ))}
                {groupedData.archived.docs.length === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <Archive className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('documents.list.noArchivedDoc')}</p>
                  </div>
                )}
              </div>
            )}
          </div>}
        </div>
      )}

    </div>
  );
}
